/**
 * Clipboard: TSV and `text/html` for copy, an Excel-grade TSV parser for paste
 * (spec M1-B4 §8.4, `[B5§6.1]`).
 *
 * Two flavours go on the clipboard, always:
 *   `text/plain` — TSV of `toClipboard` (= `toText`) per cell, rows separated by `\n`
 *   `text/html`  — a `<table>` with the same cells, so Excel and Google Sheets keep the grid
 *
 * Excel writes `\r\n` line breaks and quotes any cell containing a tab, a newline or a quote,
 * doubling embedded quotes. The parser below is the inverse of that, which is why C4 ("paste a
 * 3 × 4 block from Excel") is a unit test and not a manual check.
 */

/* ── serialise ────────────────────────────────────────────────────────────── */

/** A cell that needs quoting in Excel's dialect. */
function needsQuoting(cell: string): boolean {
  return cell.includes('\t') || cell.includes('\n') || cell.includes('\r') || cell.includes('"');
}

function quote(cell: string): string {
  return needsQuoting(cell) ? `"${cell.replaceAll('"', '""')}"` : cell;
}

export function toTsv(rows: readonly (readonly string[])[]): string {
  return rows.map((row) => row.map(quote).join('\t')).join('\n');
}

function escapeHtml(text: string): string {
  return text
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;');
}

/**
 * The `text/html` flavour. `white-space: pre-wrap` keeps multi-line cells intact in Sheets;
 * Excel reads the table structure and ignores the style.
 */
export function toHtmlTable(rows: readonly (readonly string[])[]): string {
  const body = rows
    .map(
      (row) =>
        `<tr>${row
          .map((cell) => `<td style="white-space:pre-wrap">${escapeHtml(cell).replaceAll('\n', '<br>')}</td>`)
          .join('')}</tr>`,
    )
    .join('');
  return `<table>${body}</table>`;
}

/* ── parse ────────────────────────────────────────────────────────────────── */

/**
 * Parses a TSV block the way Excel writes one. Handles `"…"` quoting, embedded tabs, embedded
 * newlines and doubled quotes; normalises `\r\n` and a bare `\r` to a row break; drops one
 * trailing empty row (Excel appends a line break to every copy).
 */
export function parseTsv(input: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;
  let index = 0;

  const pushCell = () => {
    row.push(cell);
    cell = '';
  };
  const pushRow = () => {
    pushCell();
    rows.push(row);
    row = [];
  };

  while (index < input.length) {
    const char = input[index]!;

    if (quoted) {
      if (char === '"') {
        if (input[index + 1] === '"') {
          cell += '"';
          index += 2;
          continue;
        }
        quoted = false;
        index += 1;
        continue;
      }
      cell += char;
      index += 1;
      continue;
    }

    if (char === '"' && cell === '') {
      quoted = true;
      index += 1;
      continue;
    }
    if (char === '\t') {
      pushCell();
      index += 1;
      continue;
    }
    if (char === '\r') {
      pushRow();
      index += input[index + 1] === '\n' ? 2 : 1;
      continue;
    }
    if (char === '\n') {
      pushRow();
      index += 1;
      continue;
    }
    cell += char;
    index += 1;
  }

  if (cell !== '' || row.length > 0) pushRow();
  // Excel's trailing newline produces one empty row; a genuinely empty cell keeps its row.
  while (rows.length > 1 && rows[rows.length - 1]!.length === 1 && rows[rows.length - 1]![0] === '') {
    rows.pop();
  }
  return rows;
}

/** A single-cell paste is anything that parses to one row of one cell. */
export function isSingleCell(rows: readonly (readonly string[])[]): boolean {
  return rows.length === 1 && rows[0]!.length === 1;
}

/**
 * §8.4: the block is anchored at the focused cell and **clipped** to the loaded rows and the
 * visible columns. It never creates rows or columns (ASSUMED, Q170), so C5 is this function.
 */
export function clipBlock<TRow, TColumn>(
  rows: readonly (readonly string[])[],
  targetRows: readonly TRow[],
  targetColumns: readonly TColumn[],
  anchorRow: number,
  anchorColumn: number,
): { row: TRow; column: TColumn; text: string }[] {
  const out: { row: TRow; column: TColumn; text: string }[] = [];
  for (let r = 0; r < rows.length; r += 1) {
    const row = targetRows[anchorRow + r];
    if (row === undefined) break;
    const cells = rows[r]!;
    for (let c = 0; c < cells.length; c += 1) {
      const column = targetColumns[anchorColumn + c];
      if (column === undefined) break;
      out.push({ row, column, text: cells[c]! });
    }
  }
  return out;
}

/**
 * E7: a 10,000-cell paste is chunked so no single request carries it all; one undo entry still
 * covers the whole paste because the caller keeps the batches together.
 */
export function chunk<T>(items: readonly T[], size: number): T[][] {
  if (size <= 0) throw new RangeError('chunk size must be positive');
  const out: T[][] = [];
  for (let index = 0; index < items.length; index += size) {
    out.push(items.slice(index, index + size));
  }
  return out;
}

export const PASTE_CHUNK_SIZE = 500;

/** The paste toast's sentence `[B4§8.4]`: `Pasted 34 cells · 2 skipped (Priority: …)`. */
export function pasteSummary(pasted: number, skipped: readonly string[]): string {
  const head = `Pasted ${pasted} ${pasted === 1 ? 'cell' : 'cells'}`;
  if (skipped.length === 0) return head;
  const reasons = skipped.slice(0, 2).join(', ');
  const more = skipped.length > 2 ? `, +${skipped.length - 2} more` : '';
  return `${head} · ${skipped.length} skipped (${reasons}${more})`;
}
